import React, { useEffect, useState } from 'react';
import { Button, FloatingNotification } from '@wix/design-system';
import { useTranslation } from 'react-i18next';
import { useExperiments } from '@wix/fe-essentials-standalone';
import { StatusAlert } from '@wix/wix-ui-icons-common';
import Header from '@components/Header';
import { ACTIONS, useAppContext } from '@components/Contexts/AppContext';
import useFeedbackApi from '@src/components/Hooks/useFeedbackApi';
import SiteRenderer from '@components/SiteRenderer';
import LoadingScreen from '@components/LoadingScreen';
import DisableTool from '@components/DisableTool';
import useBiLogger from '@components/Hooks/useBiLogger';
import s from './App.scss';
import '@wix/design-system/styles.global.css';
import { ADD_AUTHORIZATION_HEADERS, ANONYMOUS } from '@src/utils/constants';

export default function App() {
  const { t } = useTranslation();
  const { logPageLoaded } = useBiLogger();
  const { experiments } = useExperiments({ readOnly: true });
  const { getInitializeData } = useFeedbackApi();
  const [commentsFetchPending, setCommentsFetchPending] =
    useState<boolean>(true);

  const {
    dispatch,
    state: { errorData, loading, viewerElement, disableTool },
  } = useAppContext();
  useEffect(() => {
    logPageLoaded();
  }, [logPageLoaded]);

  useEffect(() => {
    (async () => {
      if (!experiments.enabled(ADD_AUTHORIZATION_HEADERS)) {
        setCommentsFetchPending(false);
      } else {
        try {
          const { comments: _comments, userInfo } =
            (await getInitializeData()) || {};
          if (!userInfo || !_comments) {
            return;
          }
          if (userInfo.userName === ANONYMOUS) {
            userInfo.isAnonymous = true;
          }
          dispatch({
            type: ACTIONS.SET_COMMENTS,
            payload: _comments,
          });
          dispatch({
            type: ACTIONS.SET_USER_INFO,
            payload: userInfo,
          });
          setCommentsFetchPending(false);
        } catch (e) {
          dispatch({
            type: ACTIONS.SET_ERROR,
            payload: {
              showError: true,
              errorMsgLocalKey: 'get_feedback_error',
            },
          });
          dispatch({
            type: ACTIONS.SET_USER_INFO,
            payload: { isAnonymous: true, userName: '', contactId: '' },
          });
          setCommentsFetchPending(false);
        }
      }
    })();
  }, []);

  return (
    <div data-hook="app">
      {(loading || commentsFetchPending) && <LoadingScreen />}
      {disableTool ? (
        <DisableTool />
      ) : (
        <>
          <Header />
          {errorData.showError && (
            <FloatingNotification
              onClose={() => {
                dispatch({
                  type: ACTIONS.SET_ERROR,
                  payload: { showError: false },
                });
              }}
              className={s.errorNotification}
              type="destructive"
              textButtonProps={{
                label: (
                  <Button size="tiny" skin="dark" priority="secondary">
                    {t('get_feedback_error_cta')}
                  </Button>
                ),
                onClick: () => {
                  if (viewerElement?.contentDocument) {
                    viewerElement.contentDocument.location.reload();
                  } else {
                    window.location.reload();
                  }
                  dispatch({
                    type: ACTIONS.SET_ERROR,
                    payload: { showError: false },
                  });
                  dispatch({ type: ACTIONS.SET_LOADING, payload: true });
                },
              }}
              prefixIcon={<StatusAlert />}
              text={t(errorData.errorMsgLocalKey)}
              width="fit-content"
            />
          )}
          <SiteRenderer />
        </>
      )}
    </div>
  );
}
